import { formatDate, formatDateShort } from '../../utils/dateFormat';

// Single source of truth for every workflow look-and-feel token: status
// vocabulary/colors, action button styles, event labels, doc-type badges and
// value formatters. Ratified in docs/workflow-ui.md §2 — to
// re-skin the whole workflow area, edit HERE, never in a page.

// ---------------------------------------------------------------------------
// Status vocabulary (one wording, one red — no per-flow wording splits).
//
// `awaiting_info` reads "Changes required", NOT "More info needed":
// a reviewer sends a request back both when something is missing
// AND when something is wrong, and the second case is the common one. The
// applicant's only route back is Modify → Re-submit — the badge has to name
// that, not ask for extra paperwork. The backend status key stays
// `awaiting_info` (renaming it would be a migration for zero user gain).
export const STATUS_META = {
  // QC6/QC7 only (0146): saved by the requester, not yet submitted.
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600' },
  requested: { label: 'Pending review', cls: 'bg-amber-50 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-green-50 text-green-700' },
  awaiting_info: { label: 'Changes required', cls: 'bg-red-50 text-red-700' },
  // CWCC only: the reviewer approved and QM has performed the item in the
  // external systems — the side is finished. 'approved' there means
  // "approved, awaiting QM"; the side note spells that out.
  completed: { label: 'Completed', cls: 'bg-green-50 text-green-700' },
  na: { label: '—', cls: 'bg-slate-100 text-slate-400' },
};

// External CPD files on submission — nobody approves, so its 'approved' is
// "Filed" and its 'awaiting_info' is "Returned" (the record was withdrawn).
// Same keys, same colours; only the words change. Pass as `meta` to
// WorkflowStatusBadge / `statusMeta` to StatusActionsCard.
export const CPD_STATUS_META = {
  ...STATUS_META,
  approved: { label: 'Filed', cls: 'bg-green-50 text-green-700' },
  awaiting_info: { label: 'Returned', cls: 'bg-red-50 text-red-700' },
};

// ---------------------------------------------------------------------------
// Flow display names (workflow-contract.md §4.1: one Title Case name per
// flow, no request/declaration tail). Keyed by the union `kind` the aggregate
// feeds return (my-updates / overview); qc splits on qcType.
export const FLOW_LABELS = {
  client: 'New Client',
  qc: 'QC6 / QC7 Forms',
  qc6: 'QC6 – Client Acceptance',
  qc7: 'QC7 – Continuance',
  engreg: 'Engagement Registration',
  cwcc: 'CaseWare, Charge Code & Team Update',
  esc: 'Engagement Status Change',
  screening: 'Screening',
  fs: 'Signed Financial Statements',
  qc35: 'QC35 Form',
  car: 'Client Amendment',
  qca: 'QC Amendment',
  qcad: 'QC6 / QC7 Addendum',
  ear: 'Engagement Amendment',
  fsa: 'Signed Financial Statements Amendment',
  qc35a: 'QC35 Form Amendment',
  cpd: 'External CPD',
  pq: 'Professional Qualification',
  absence: 'Training Absence',
  dispute: 'Attendance Dispute',
};
export const flowLabel = (kind, qcType) =>
  FLOW_LABELS[kind === 'qc' && qcType ? (qcType === 'qc7' ? 'qc7' : 'qc6') : kind] || kind;

// ---------------------------------------------------------------------------
// Action buttons — one color per verb, everywhere:
// Approve green (the only green button), Return for Changes red, Re-submit
// blue, Modify outline, Delete red-outline.
// The red button reads "Return for Changes", not "Reject":
// the request is not refused, it goes back to the applicant and returns. The
// internal names (onReject / btnReject / POST :id/reject) keep that verb —
// the API contract and fifteen route files are not worth churning for a word.
export const btnApprove = 'inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed';
export const btnReject = 'inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50';
export const btnResubmit = 'inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50';
export const btnOutline = 'inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50';
export const btnDeleteOutline = 'inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 text-sm font-medium rounded-lg hover:bg-red-50 disabled:opacity-50';
// Compact in-field action (sits in the <Field action> label slot), e.g. the
// Signed Financial Statements "Auto-detect from PDF" button.
export const btnFieldAction = 'inline-flex items-center gap-1.5 px-2.5 py-1 border border-blue-200 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed';

// ---------------------------------------------------------------------------
// The reviewer's return reason — surfaced here because if it were only
// visible after expanding History, the one person who has to act on it would
// never see it.
// Red on purpose — it is the same state as the `awaiting_info` badge sitting
// right above it, and amber already means "Pending review". Rendered by
// StatusActionsCard, directly above the Modify / Re-submit buttons it explains.
//
// The heading says "Reviewer's reason", NOT "Returned for changes":
// the badge one line up already says "Changes required", and a
// box that repeats the state instead of naming its contents reads as noise.
// Badge = what state this is; box = why.
export const calloutReturned = 'mt-4 rounded-lg border border-red-200 bg-red-50 p-3';
export const calloutReturnedTitle = 'text-xs font-semibold text-red-800 uppercase tracking-wide';
export const calloutReturnedBody = 'text-sm text-red-900 mt-1 whitespace-pre-wrap break-words';
export const calloutReturnedMeta = 'text-xs text-red-700 opacity-75 mt-1.5 break-words';

// ── 結案鎖定的說明框（docs/workflow-contract.md §2d）───────────────────────
// 琥珀而不是紅：不是「你做錯了要改」，是「這案子已經結了，動作暫停」。
// 由 StatusActionsCard 渲染在它藏起來的 Approve／Modify／Re-submit 原位。
export const calloutLocked = 'mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3';
export const calloutLockedTitle = 'text-xs font-semibold text-amber-800 uppercase tracking-wide';

// ── 記錄頁頂端「還缺一份簽署檔」的一句話（header 與動作卡之間）───────────
// 琥珀而不是紅：還沒補不等於做錯，紅色留給「你要改」（calloutReturned）。
// 沒有圖示——一行字自己說得完，一顆小圖只是裝飾。
// mb-4 是必要的——它下面的 RecordActionsCard／SectionCard 都自帶 mb-4，這裡
// 不給就與白卡貼死。
export const recordNotice = 'rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 mb-4';

// ── 核准前審核者自己要先做的一件事 ──────────────────────────────────────────
// 目前只有 Eng Reg 的 QM screening 文件：審核者要親手附上 PDF 才准核准。
// 若把上傳欄位放在頁面中段的 Screening 卡裡——按鈕在最上面、灰的——
// 要按的人不捲到一半根本不知道自己缺什麼。所以這種「解鎖用」的輸入一律
// 由 StatusActionsCard 的 beforeActions 顯示在按鈕正上方。
//
// 沒有底色、沒有外框（不做整塊藍底 callout——要簡單且清楚）：
// 它跟下面那排按鈕是同一件事——一行標題、一行條件、
// 一顆選檔鈕。清楚靠的是位置與紅色星號，不是一塊色。
export const actionPrereqLabel = 'text-sm font-medium text-slate-900';
export const actionPrereqHint = 'text-xs text-slate-500 mt-0.5';
// 選檔鈕：原生 file input 由瀏覽器語系決定字（Windows 上是「選擇檔案／未選擇
// 任何檔案」），一頁英文裡冒出兩個中文按鈕。所以 input 藏起來、用 label 包一顆
// 我們自己的鈕，字自己寫，長相與 Modify/Delete 那排一致。
export const btnChooseFile = 'inline-flex items-center gap-2 px-3 py-1.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 cursor-pointer shrink-0';

// ── 退件的逐項意見 ─────────────────────────────────────────────────────────
// 同一個紅，但比整體原因輕：意見是貼在某一格旁邊的便條，不是這張單的判決。
// 所以只有左邊一條紅線 + 淺底，沒有外框——外框會讓每一格看起來都在報錯。
export const fieldCommentBox = 'mt-1.5 border-l-2 border-red-300 bg-red-50/60 pl-2 py-1 rounded-r';
export const fieldCommentText = 'text-xs text-red-900 whitespace-pre-wrap break-words';
export const fieldCommentMeta = 'text-[10px] text-red-700 opacity-75 uppercase tracking-wide';
// 「這一項申請者已經改過了」——琥珀而非綠：它陳述有動作發生，不是說已經改對，
// 改對與否仍然由審核者判斷（同 chart-status 的兩值規則，未達標用琥珀不用紅）。
export const fieldCommentUpdated = 'text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px';
// DetailItem 上的「＋ Comment」——平時隱形，游標移到那一格才浮出來，
// 已經寫了字就一直留著。審核頁一次顯示幾十格，全部常駐會蓋掉內容本身。
export const fieldCommentAddBtn = 'text-[10px] text-slate-500 hover:text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity';
export const fieldCommentAddBtnActive = 'text-[10px] text-red-600 hover:text-red-700';
// 就地展開的小輸入框（DetailItem 與 RejectModal 共用），比 FormKit 的
// inputCls 小一號：它長在一格資料底下，不是一張表單的欄位。
export const inputXs = 'w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400';

// ── QC 逐題的 Word 附註（QcTip）──────────────────────────────────────────
// 圖示：平時淡藍、展開中深藍實心底，讓人看得出這一題的附註正攤在格子裡。
export const qcTipIcon = 'text-blue-400 hover:text-blue-600 focus:text-blue-600 focus:outline-none';
export const qcTipIconActive = 'text-white bg-blue-600 rounded-full hover:bg-blue-700 focus:outline-none';
// 滑過浮出的深底 tooltip（寬版、可捲動）。
export const qcTipPopover = 'absolute top-full mt-1.5 z-30 w-[26rem] max-w-[80vw] max-h-[28rem] overflow-y-auto rounded-md bg-slate-800 px-3 py-2 text-xs font-normal normal-case tracking-normal leading-relaxed text-white shadow-lg space-y-1.5';
// 點開後就地攤在題目底下的附註區塊：淡藍底、小字、正常字重（區段標題是粗體，
// 附註不跟著粗）。
export const qcTipPanel = 'mt-1.5 rounded-md border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs font-normal normal-case tracking-normal leading-relaxed text-slate-700 space-y-1.5';
export const qcTipPanelHide = 'block ml-auto text-[10px] font-medium text-blue-600 hover:text-blue-800';
// 表頭右上「Show all guidance / Hide all guidance」，長相同 btnFieldAction 那級的小鈕。
export const btnQcTipAll = 'inline-flex items-center gap-1.5 px-2.5 py-1 border border-blue-200 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-50';

// ── Addenda 清單的「最新一份」──────────────────────────────────────────────
// 一份 QC 記錄可掛多份附錄、風險評級是串鏈改的——現行評級由最新那份決定，
// 所以清單裡最新一份用淺藍框＋「Latest」小標抬出來（藍是資訊色，
// 綠/紅留給審核結果），其餘維持素框。AddendumRow 渲染、兩個列表面共用。
export const latestAddendumCard = 'border-blue-300 bg-blue-50/50';
export const latestAddendumChip = 'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 shrink-0';

// ── 進度看板卡頭 ──────────────────────────────────────────────────────────
// 卡＝集團＋FYE 年份：年份用靛紫淺底章，服務一個一個分開的灰框章，前面各有
// 小標，一眼分得出哪個是年度、哪些是服務。藍色留給上方的 Status 篩選晶片，
// 綠／琥珀／紅留給關卡狀態。
export const boardCardLabel = 'text-[10px] font-semibold uppercase tracking-wide text-slate-400';
export const boardFyChip = 'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border border-indigo-200 bg-indigo-50 text-indigo-700 whitespace-nowrap';
export const boardServiceChip = 'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border border-slate-200 bg-slate-50 text-slate-700 whitespace-nowrap';

// ---------------------------------------------------------------------------
// History event labels: [label, badge classes]. Flow-specific wording (e.g.
// applied → "Applied to QC record") is passed to <HistoryCard actionLabels>
// as an override; this map is the shared base.
export const ACTION_LABEL = {
  draft_saved: ['Draft saved', 'bg-slate-100 text-slate-600'],
  submitted: ['Submitted', 'bg-blue-50 text-blue-700'],
  modified: ['Modified', 'bg-slate-100 text-slate-600'],
  resubmitted: ['Re-submitted', 'bg-blue-50 text-blue-700'],
  approved: ['Approved', 'bg-green-50 text-green-700'],
  rejected: ['Returned', 'bg-red-50 text-red-700'],
  approved_eng: ['Engagement approved', 'bg-green-50 text-green-700'],
  approved_cw: ['CaseWare side approved', 'bg-green-50 text-green-700'],
  approved_cc: ['Charge Code side approved', 'bg-green-50 text-green-700'],
  completed_cw: ['CaseWare completed by QM', 'bg-green-50 text-green-700'],
  completed_cc: ['Charge Code completed by QM', 'bg-green-50 text-green-700'],
  completed_member_cw: ['Team update done in CaseWare', 'bg-green-50 text-green-700'],
  completed_member_cc: ['Team update done for the charge code', 'bg-green-50 text-green-700'],
  completed_member: ['Team update completed by QM', 'bg-green-50 text-green-700'],
  engagement_created: ['Engagement created', 'bg-green-50 text-green-700'],
  // Screening: the registration's approval opened the QM request; QM completed it.
  screening_requested: ['Screening request opened', 'bg-teal-50 text-teal-700'],
  completed: ['Completed by QM', 'bg-green-50 text-green-700'],
  record_created: ['Screening record created', 'bg-green-50 text-green-700'],
  client_created: ['Registered in master', 'bg-green-50 text-green-700'],
  applied: ['Applied to master', 'bg-green-50 text-green-700'],
  signoff_stamped: ['Sign-off stamped', 'bg-green-50 text-green-700'],
  approval_recorded: ['Approval recorded', 'bg-emerald-50 text-emerald-700'],
  ai_approved: ['Approved by AI review', 'bg-green-50 text-green-700'],
};

// Every flow diffs its attachments under the same key (server-side
// attachmentEdits.attachmentDiff), so the label belongs here rather than in
// fifteen per-page CHANGE_LABELS maps. Shared by HistoryCard and
// ChangesSinceReturnCard — the two must never name a field differently.
export const SHARED_CHANGE_LABELS = { files: 'Attachments', reviewer: 'Reviewer' };

// ---------------------------------------------------------------------------
// Attachment doc-type badges: [label, badge classes]. Keys cover both the
// request-file docTypes (workflow uploads) and engagement/QC record docTypes.
export const DOC_TYPE_BADGE = {
  QC6: ['QC6', 'bg-blue-50 text-blue-700'],
  QC7: ['QC7', 'bg-blue-50 text-blue-700'],
  QC7_PREV: ['Previous year QC', 'bg-amber-50 text-amber-700'],
  GROUP_STRUCTURE: ['Group structure', 'bg-violet-50 text-violet-700'],
  QC_FORM: ['QC document', 'bg-blue-50 text-blue-700'],
  FS: ['Signed Financial Statements', 'bg-blue-50 text-blue-700'],
  fs: ['Signed Financial Statements', 'bg-blue-50 text-blue-700'],
  QC35: ['QC35 Form', 'bg-blue-50 text-blue-700'],
  qc35: ['QC35 Form', 'bg-blue-50 text-blue-700'],
  screenshot: ['CaseWare screenshot', 'bg-purple-50 text-purple-700'],
  SUPPLEMENTARY: ['Supplementary', 'bg-slate-100 text-slate-500'],
  APPOINTMENT_LETTER: ['Appointment letter', 'bg-emerald-50 text-emerald-700'],
  BIZFILE: ['BizFile', 'bg-indigo-50 text-indigo-700'],
  SCREENING: ['Screening', 'bg-teal-50 text-teal-700'],
};
export const SUPPORTING_BADGE = ['Supporting', 'bg-slate-100 text-slate-600'];
export const docTypeBadge = (docType) => DOC_TYPE_BADGE[docType] || SUPPORTING_BADGE;

// ---------------------------------------------------------------------------
// Human labels for amendment payload fields (QCA + CAR "Requested changes"
// tables) — moved from WorkflowQca.js.
// ── Engagement pipeline ────────────────────────────────────────────────────
// Engagement Progress 的六個關卡 chip。用語跟 STATUS_META 一致：同一個底層
// 狀態在看板和詳情頁講同一個詞。`Done` 與 `n/a` 是看板專有——它們描述一個
// **關卡**而不是一張申請單，而一個關卡可以因為被歸檔而完成，不是因為被核准。
export const STAGE_META = {
  done: { label: 'Done', cls: 'bg-green-50 text-green-700' },
  pending: { label: 'Pending review', cls: 'bg-amber-50 text-amber-700' },
  // CW / CC / Team 關限定（cwcc 兩段制）：已核准、QM 還沒在外部系統執行完——
  // 還不是 Done。同琥珀（都是「等別人」），字說清楚在等誰。
  qm: { label: 'Awaiting QM', cls: 'bg-amber-50 text-amber-700' },
  returned: { label: 'Changes required', cls: 'bg-red-50 text-red-700' },
  // none（待辦）與 na（不適用）畫面上長一樣（2026-09-16 業主拍板：沒資料的關卡
  // 一律同一種灰、同一個「—」），差別只放在滑鼠提示 `hint`；後端狀態語意不變，
  // Current stage 仍只把 none 當待辦、跳過 na。
  none: { label: '—', cls: 'bg-slate-100 text-slate-400', hint: 'No request filed yet' },
  na: { label: '—', cls: 'bg-slate-100 text-slate-400', hint: 'Not applicable to this engagement' },
};

// Current stage（「卡在哪」的總結）的顏色。關卡句型**重用上面 chip 的三色**，
// 所以總結 chip 天然指向它在講哪一格：
//   pending  = 送了在審（等別人）      → 琥珀，同 pending 關卡
//   returned = 被退回（該我動手）      → 紅，同 returned 關卡
//   todo     = 還沒送件（該我送出）    → 灰
//   finished = Finished（沒有下一步）  → 深一階的灰——也涵蓋停掉的案，不代表好壞
// label 由後端算好（services/engagementStage），前端只挑顏色——前端各自
// 判斷是兩個畫面分歧的另一個入口。
export const STAGE_TONE_CLS = {
  pending: 'bg-amber-50 text-amber-700',
  returned: 'bg-red-50 text-red-700',
  todo: 'bg-slate-100 text-slate-600',
  finished: 'bg-slate-200 text-slate-700',
};

// `yearEnd`、`qcDocument` 與 `entities` **不會再有新的**（FYE
// 跟著 engagement、簽署文件不再走替換、涵蓋改挑案子），但既有已核准
// 的單仍帶著它們，所以標籤永久保留——拿掉只會讓舊單的差異表印出裸 key。
export const AMENDMENT_FIELD_LABELS = {
  engagements: 'Engagements Covered',
  entities: 'Companies Covered',
  yearEnd: 'Financial Year End',
  answers: 'QC6 / QC7 Form Answers',
  remarks: 'Remarks',
  qcDocument: 'Signed QC Document',
  entityName: 'Entity Name',
  uen: 'UEN',
  clientGroup: 'Client Group',
  groupName: 'Client Group Name',
  dateOfAppointment: 'Date of Appointment',
  active: 'Relationship',
  inactiveDate: 'Inactive Date',
  inactiveReason: 'Inactive Reason',
};

// The calendar year of an FYE ('2025-12-31' → '2025'), or '' when unset.
// Mirrors the server's fyeYear (server/routes/qcPickRules.js): the acceptance
// QC a Setup/Amendment picks must be of the SAME FYE year as the engagement,
// so the pickers filter their feed by it and say so.
export const fyeYearOf = (yearEnd) => {
  const m = /^(\d{4})/.exec(String(yearEnd || '').trim());
  return m ? m[1] : '';
};

// Option label for the Setup/EAR "pick a QC record" dropdowns. A record
// covering several same-group companies reads as
// "Group · N companies — FYE …"; a single-company record keeps the entity name.
export const qcRecordOptionLabel = (q) => {
  const covered = (q.entities || []).length > 1
    ? `${q.clientGroup || q.entityName} · ${q.entities.length} companies`
    : q.entityName;
  return `${covered} — FYE ${formatDateShort(q.yearEnd)}${q.sourceRequestNo ? ` (${q.sourceRequestNo})` : ''}`;
};

// ── 多筆 engagement 的顯示 ─────────────────────────────────────────────────
// 組合案名「Entity - UEN - FYE31-12-2025」串成一行完全讀不動。
// 資料層（DB payload／稽核字串／信件）維持組合名
// 不動——顯示層一律走 CoverageDisplay.js：公司名當主體、UEN/FYE 降成淡色
// 小字、多筆一筆一行；涵蓋差異加 Added/Removed 徽章（綠＝加入、紅＝離開——
// 紅在這裡是「移除」不是狀態）。
// 詳情頁的 label／value 閱讀版（DetailItem 與所有手刻的同型格共用）。
// label 是一顆淺灰小徽章、value 深黑半粗：兩者形狀不同，不再靠字級分辨。
// 分區小標（Team Members、Addenda…）與表頭不用這組——它們是段落標題，
// 維持純文字大寫，跟欄位徽章正好是兩種形狀。
export const detailLabel = 'inline-block text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide leading-[14px] bg-slate-100 px-1.5 py-0.5 rounded';
export const detailValue = 'text-sm font-medium text-slate-900 mt-1 break-words';
export const detailGridGap = 'gap-x-6 gap-y-5';

// 詳情頁的分區標題（SectionCard 卡片標題、Engagement 主檔的 Engagement／Team…）：
// 大寫粗體＋左側藍條，要一眼跟欄位資料分開。
export const sectionTitle = 'flex items-center gap-2.5 text-base font-bold text-slate-900 uppercase tracking-wide';
export const sectionTitleBar = 'w-1 h-5 rounded-full bg-blue-600 shrink-0';
// 彈窗／卡片內的分段小標：彈窗本身已有大標題，所以小一級（14px）但同樣粗體大寫＋藍條。
export const subsectionTitle = 'flex items-center gap-2 text-sm font-bold text-slate-900 uppercase tracking-wide';
export const subsectionTitleBar = 'w-1 h-4 rounded-full bg-blue-600 shrink-0';

// UEN／FYE 一律獨立一行，不接在公司名後面——inline 折行時會掉到下一行開頭，
// 看起來像另一個欄位。
export const coverageMeta = 'block text-[11px] font-normal text-slate-500';
// 「(now: …)」——記錄上的公司名／UEN／集團名是寫入當下的快照、永不改寫；主檔
// 之後改名時，現名以灰字括號接在後面（shared/NowName.js）。淡而不搶：主體
// 仍是記錄當時的名字，括號只是導引。
export const nameNow = 'ml-1 text-[11px] font-normal text-slate-400 whitespace-nowrap';
// 多案清單（Engagement(s) Covered、差異表、Records 展開列）：與按案分格的
// SplitList 同一個外框——圓角細框、列與列分隔線，公司名 14px 粗一級。
export const coverageList = 'rounded-lg border border-slate-200 bg-white divide-y divide-slate-200';
export const coverageListRow = 'flex flex-wrap items-center gap-x-1.5 gap-y-0.5 px-3 py-2 text-sm font-medium text-slate-800';
// 同一個外框裡內容自由排版的列（逐案評級＋理由、同批送出的單據）。
export const coverageListItem = 'px-3 py-2 text-sm text-slate-800 break-words';
export const coverageBadgeAdded = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 shrink-0';
export const coverageBadgeRemoved = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 shrink-0';
// 清單列的「N engagements」展開鈕與展開出來的子列（WorkflowListShell 的 expandRow）。
export const rowExpandToggle = 'mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline';
export const rowExpandPanel = 'bg-slate-50 px-4 py-3';

// `answers` never reaches here: HistoryCard's `expandChanges` unpacks
// it into one row per question first (qcQuestions.expandAnswerChanges).
// The object guard stays as a backstop — printing
// "[object Object]" in an audit trail is worse than saying nothing.
export const fmtAmendValue = (field, v) => {
  // Relationship travels as the master's boolean `active`.
  if (field === 'active') return v === true ? 'Active' : v === false ? 'Inactive' : '—';
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'yearEnd' || field === 'inactiveDate' || field === 'dateOfAppointment') return formatDate(v);
  if (typeof v === 'object') return '(see the comparison in the request above)';
  return String(v);
};

// ---------------------------------------------------------------------------
// One byte formatter (was 5 copies with 2 algorithms; this is the
// EngagementAttachments 3-tier version).
export const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ── 送出時的必填標示 ─────────────────────────────────────────────────────
// 按 Submit 才檢查；沒填的那一格就地標出來：label 轉紅、整格一圈淡紅光暈、
// 控制項底下一行「Required」。光暈用 ring-offset 做，不改版面、不擠動旁邊的
// 欄位；同一格底下也可能同時有退件意見（紅便條），所以這裡只用邊框不用底色。
export const fieldInvalidRing = 'rounded-md ring-2 ring-red-300 ring-offset-4 ring-offset-white';
export const fieldInvalidLabel = 'text-red-700';
export const fieldInvalidText = 'text-xs text-red-600 mt-1';

// 按案／按公司分開填（費用、預計出 FS 日）：預設收起，Total 或共用日期底下一行小字
// 連結切換；展開後是一張分隔線清單（SplitList），每列左邊公司名＋灰字服務、右邊
// 輸入框，費用的最後一列是 Total（幣別＋加總）。Registration 與 QC 表單共用。
export const linkSplitToggle = 'mt-1.5 inline-flex text-left text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline';
export const splitList = 'rounded-lg border border-slate-200 bg-white divide-y divide-slate-200';
export const splitListRow = 'flex items-center gap-3 px-3 py-2';
export const splitListTitle = 'text-sm font-medium text-slate-800 break-words';
export const splitListSubtitle = 'text-xs text-slate-500 break-words';
// 輸入框寬：金額窄（名稱多留空間）、日期要放得下 yyyy/mm/dd＋日曆鈕。
export const splitListAmount = 'w-28 shrink-0';
export const splitListDate = 'w-40 shrink-0';
// 唯讀檢視：值靠右、不限寬。
export const splitListReadValue = 'shrink-0 text-right text-sm text-slate-800 tabular-nums';
export const splitListFooter = 'flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-b-lg';
export const splitListTotal = 'text-sm font-semibold text-slate-800 tabular-nums text-right';
// 選項／文字題按案分開答（PIE、評級、理由）：控制項放在公司名底下（單選三顆、
// 文字框放不進右側窄欄）。
export const splitListStackedRow = 'px-3 py-2 space-y-1.5';
// 服務清單（Service Provided 展開成每家一列）：公司自己的列白底不可移除，底下加的
// Additional Service 列灰底、標題旁一顆小標籤、右側 Remove。
export const splitListExtraRow = 'px-3 py-2 space-y-1.5 bg-slate-50';
export const splitListExtraTag = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-600 uppercase tracking-wide';

// 一家公司一個分頁（Engagement Amendment 的多案、Signed Financial Statements 一頁多家、
// Management FS 記錄一次建多筆）：分段藥丸，active 白底浮起；名字完整顯示、過長換行
// 不截斷（有客戶名只差最後幾個字，截掉就分不出來）。
// 分頁上的狀態小章分三色：完成（綠）、待人工（藍）、還沒填完（琥珀）。
export const companyTabBar = 'flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1';
export const companyTab = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors';
export const companyTabActive = 'bg-white text-blue-700 shadow-sm border border-slate-200';
export const companyTabIdle = 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60';
export const companyTabLabel = 'max-w-[320px] break-words text-left';
// 同公司多案時名字下方第二行（服務／描述，見 CoverageDisplay.engagementLabelsOf）。
export const companyTabStack = 'flex flex-col items-start min-w-0 leading-tight text-left';
export const companyTabSub = 'max-w-[320px] break-words text-[11px] font-normal text-slate-400';
export const companyTabTag = {
  done: 'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800',
  info: 'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800',
  todo: 'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800',
  muted: 'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 text-slate-500',
};
